import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { logAudit } from '../../../lib/audit';
import {
  useAdminSuggestions,
  SUGGESTION_CATEGORIES,
  SUGGESTION_STATUS_META,
  type Suggestion,
  type SuggestionStatus,
} from '../../../hooks/useSuggestions';

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });

const ALL_STATUSES: SuggestionStatus[] = [
  'em_analise', 'aprovada', 'planejada', 'em_desenvolvimento', 'concluida', 'recusada',
];

const PUBLISHED_STATUSES: SuggestionStatus[] = ['aprovada', 'planejada', 'em_desenvolvimento', 'concluida'];

const statusDark: Record<string, string> = {
  em_analise:         'bg-yellow-500/20 text-yellow-400',
  aprovada:           'bg-emerald-500/20 text-emerald-400',
  planejada:          'bg-sky-500/20 text-sky-400',
  em_desenvolvimento: 'bg-violet-500/20 text-violet-400',
  concluida:          'bg-emerald-700/20 text-emerald-300',
  recusada:           'bg-red-500/20 text-red-400',
};

export default function AdminSugestoesPage() {
  const { suggestions, loading, updateSuggestion, deleteSuggestion } = useAdminSuggestions();

  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCat, setFilterCat] = useState('all');
  const [selected, setSelected] = useState<Suggestion | null>(null);
  const [newStatus, setNewStatus] = useState<SuggestionStatus>('em_analise');
  const [modNotes, setModNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [profileMap, setProfileMap] = useState<Record<string, { name: string; email: string }>>({});
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<Suggestion | null>(null);
  const [deleteStep, setDeleteStep] = useState<1 | 2>(1);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (suggestions.length === 0) return;
    const ids = [...new Set(suggestions.map(s => s.user_id))];
    supabase.from('profiles').select('id, full_name, display_name, email')
      .in('id', ids)
      .then(({ data }) => {
        const map: Record<string, { name: string; email: string }> = {};
        (data ?? []).forEach((p: { id: string; full_name: string; display_name: string; email: string }) => {
          map[p.id] = { name: p.full_name || p.display_name || 'Usuário', email: p.email || '' };
        });
        setProfileMap(map);
      });
  }, [suggestions]);

  const filtered = suggestions.filter(s => {
    if (filterStatus !== 'all' && s.status !== filterStatus) return false;
    if (filterCat !== 'all' && s.category !== filterCat) return false;
    return true;
  });

  const totalVotes = suggestions.reduce((acc, sg) => acc + sg.votes_count, 0);

  const counts = {
    total:       suggestions.length,
    em_analise:  suggestions.filter(s => s.status === 'em_analise').length,
    aprovadas:   suggestions.filter(s => ['aprovada', 'planejada', 'em_desenvolvimento'].includes(s.status)).length,
    concluidas:  suggestions.filter(s => s.status === 'concluida').length,
  };

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  const openDetail = (s: Suggestion) => {
    setSelected(s);
    setNewStatus(s.status);
    setModNotes(s.moderation_notes ?? '');
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    await updateSuggestion(selected.id, newStatus, modNotes.trim() || undefined);
    setSaving(false);
    setSelected(null);
    showToast('Sugestão atualizada!');
  };

  const quickAction = async (s: Suggestion, status: SuggestionStatus, e: React.MouseEvent) => {
    e.stopPropagation();
    await updateSuggestion(s.id, status);
    showToast(`Sugestão ${SUGGESTION_STATUS_META[status].label.toLowerCase()}!`);
  };

  // ── Delete flow ─────────────────────────────────────────────────────────
  const requiresExtraConfirm = (s: Suggestion) =>
    PUBLISHED_STATUSES.includes(s.status) || s.votes_count > 0;

  const openDeleteConfirm = (s: Suggestion, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget(s);
    setDeleteStep(1);
  };

  const handleDeleteConfirmStep1 = () => {
    if (!deleteTarget) return;
    if (requiresExtraConfirm(deleteTarget)) {
      setDeleteStep(2);
    } else {
      handleDeleteExecute();
    }
  };

  const handleDeleteExecute = async () => {
    if (!deleteTarget) return;
    setDeleting(true);

    const authorName = profileMap[deleteTarget.user_id]?.name ?? 'Usuário';
    const { error } = await deleteSuggestion(deleteTarget.id);

    if (error) {
      setDeleting(false);
      showToast('Erro ao excluir sugestão.', 'error');
      setDeleteTarget(null);
      return;
    }

    // Auditoria
    await logAudit({
      action: 'delete',
      resource: 'suggestion' as any,
      resource_id: deleteTarget.id,
      details: {
        title:      deleteTarget.title,
        category:   deleteTarget.category,
        status:     deleteTarget.status,
        votes:      deleteTarget.votes_count,
        author:     authorName,
        author_id:  deleteTarget.user_id,
      },
    });

    setDeleting(false);
    setDeleteTarget(null);
    // Close detail modal if it was the same suggestion
    if (selected?.id === deleteTarget.id) setSelected(null);
    showToast('Sugestão excluída com sucesso.');
  };

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 px-4 py-3 rounded-xl text-sm font-500 flex items-center gap-2 pointer-events-none text-white ${toast.type === 'error' ? 'bg-red-500' : 'bg-emerald-500'}`}>
          <i className={toast.type === 'error' ? 'ri-error-warning-line' : 'ri-check-line'} />
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-[24px] font-700 text-white">Sugestões</h1>
          <p className="text-white/40 text-[14px] mt-1">Moderação e gestão das sugestões dos usuários</p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/60 text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line" />Atualizar
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total',            value: counts.total,      icon: 'ri-lightbulb-line',       color: 'bg-white/10',    text: 'text-white',         sub: 'Todas as sugestões' },
          { label: 'Em análise',       value: counts.em_analise, icon: 'ri-search-eye-line',       color: 'bg-yellow-600',  text: 'text-yellow-300',    sub: 'Aguardando moderação' },
          { label: 'Aprovadas/Ativas', value: counts.aprovadas,  icon: 'ri-checkbox-circle-line',  color: 'bg-emerald-600', text: 'text-emerald-300',   sub: 'Visíveis publicamente' },
          { label: 'Total de votos',   value: totalVotes,        icon: 'ri-thumb-up-line',         color: 'bg-violet-600',  text: 'text-violet-300',    sub: 'Em todas as sugestões' },
        ].map(s => (
          <div key={s.label} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-4">
            <div className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 ${s.color}`}>
              <i className={`${s.icon} text-white text-[20px]`} />
            </div>
            <div>
              <p className="text-[11px] text-white/40 mb-0.5">{s.label}</p>
              <p className={`text-[22px] font-700 ${s.text}`}>{s.value}</p>
              <p className="text-[11px] text-white/25">{s.sub}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-5 space-y-3">
        <div className="flex gap-2 flex-wrap items-center">
          <span className="text-[11px] text-white/30 uppercase tracking-wider font-600 mr-1">Status</span>
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-3 py-1.5 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${filterStatus === 'all' ? 'bg-violet-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
          >
            Todas
          </button>
          {ALL_STATUSES.map(s => {
            const meta = SUGGESTION_STATUS_META[s];
            return (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${filterStatus === s ? 'bg-violet-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
              >
                {meta.label}
              </button>
            );
          })}
        </div>
        <div className="flex gap-3 flex-wrap items-center">
          <span className="text-[11px] text-white/30 uppercase tracking-wider font-600 mr-1">Categoria</span>
          <select
            value={filterCat}
            onChange={e => setFilterCat(e.target.value)}
            className="bg-white/5 border border-white/10 text-white/60 text-[12px] rounded-xl px-3 py-1.5 outline-none cursor-pointer"
          >
            <option value="all">Todas as categorias</option>
            {SUGGESTION_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>

      {/* List */}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line text-2xl text-violet-400 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-white/30">
            <i className="ri-lightbulb-line text-3xl mb-2 block" />
            <p className="text-[14px]">Nenhuma sugestão encontrada</p>
            <p className="text-[12px] mt-1 text-white/20">
              {suggestions.length === 0 ? 'Nenhuma sugestão enviada ainda.' : 'Nenhum resultado para os filtros aplicados.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {filtered.map(s => {
              const meta   = SUGGESTION_STATUS_META[s.status];
              const cat    = SUGGESTION_CATEGORIES.find(c => c.value === s.category);
              const author = profileMap[s.user_id];
              return (
                <div key={s.id} className="px-5 py-4 hover:bg-white/[0.03] transition-colors">
                  <div className="flex items-center gap-4">
                    {/* Content */}
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openDetail(s)}>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="text-[13px] font-700 text-white">{s.title}</p>
                        <span className={`text-[10px] font-600 px-2 py-0.5 rounded-full flex items-center gap-1 ${statusDark[s.status] || 'bg-white/10 text-white/40'}`}>
                          <i className={`${meta?.icon ?? 'ri-circle-line'} text-[9px]`} />
                          {meta?.label ?? s.status}
                        </span>
                      </div>
                      <p className="text-[12px] text-white/50 truncate">{s.description}</p>
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        <span className="text-[11px] text-white/30">{author?.name ?? 'Usuário'}</span>
                        {cat && <span className="text-[11px] text-white/30">{cat.label}</span>}
                        <span className="text-[11px] text-white/30">{fmtDate(s.created_at)}</span>
                        <span className="flex items-center gap-1 text-[11px] text-white/50 font-600">
                          <i className="ri-thumb-up-line text-[11px]" />{s.votes_count}
                          {(s.downvotes_count ?? 0) > 0 && (
                            <span className="flex items-center gap-0.5 text-red-400/70 ml-1">
                              <i className="ri-thumb-down-line text-[11px]" />{s.downvotes_count}
                            </span>
                          )}
                        </span>
                        {PUBLISHED_STATUSES.includes(s.status) && (
                          <span className="text-[10px] text-emerald-400 font-600 flex items-center gap-1">
                            <i className="ri-eye-line text-[10px]" />Publicada
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Quick actions */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {s.status === 'em_analise' && (
                        <>
                          <button
                            onClick={(e) => quickAction(s, 'aprovada', e)}
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-xl text-[11px] font-600 cursor-pointer whitespace-nowrap transition-colors"
                          >
                            <i className="ri-check-line" />Aprovar
                          </button>
                          <button
                            onClick={(e) => quickAction(s, 'recusada', e)}
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-white/5 hover:bg-white/10 text-white/50 rounded-xl text-[11px] font-600 cursor-pointer whitespace-nowrap transition-colors"
                          >
                            <i className="ri-close-line" />Recusar
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => openDetail(s)}
                        className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/15 rounded-lg transition-colors cursor-pointer"
                        title="Editar"
                      >
                        <i className="ri-edit-2-line text-white/50 text-[13px]" />
                      </button>
                      <button
                        onClick={(e) => openDeleteConfirm(s, e)}
                        className="w-8 h-8 flex items-center justify-center bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-colors cursor-pointer"
                        title="Excluir sugestão"
                      >
                        <i className="ri-delete-bin-line text-red-400 text-[13px]" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="px-5 py-3 border-t border-white/5 text-[12px] text-white/30">
          {filtered.length} sugestão{filtered.length !== 1 ? 'ões' : ''} exibida{filtered.length !== 1 ? 's' : ''}
          {suggestions.length !== filtered.length && ` (de ${suggestions.length} total)`}
        </div>
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between p-5 border-b border-white/10 sticky top-0 bg-[#1a1a2e]">
              <div className="flex-1 min-w-0 pr-3">
                <h2 className="text-[16px] font-700 text-white truncate">{selected.title}</h2>
                <p className="text-[12px] text-white/30 mt-0.5">
                  Por: {profileMap[selected.user_id]?.name ?? 'Usuário'} ({profileMap[selected.user_id]?.email}) · {selected.votes_count} votos
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={(e) => openDeleteConfirm(selected, e)}
                  className="w-8 h-8 flex items-center justify-center bg-red-500/10 hover:bg-red-500/20 rounded-lg cursor-pointer transition-colors"
                  title="Excluir sugestão"
                >
                  <i className="ri-delete-bin-line text-red-400 text-[13px]" />
                </button>
                <button
                  onClick={() => setSelected(null)}
                  className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-white/60" />
                </button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {/* Description */}
              <div className="bg-white/5 rounded-xl p-4">
                <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-2">Descrição</p>
                <p className="text-[13px] text-white/80 leading-relaxed whitespace-pre-wrap">{selected.description}</p>
              </div>

              {/* Status picker */}
              <div>
                <p className="text-[12px] text-white/40 font-600 uppercase tracking-wider mb-2">Alterar status</p>
                <div className="grid grid-cols-2 gap-2">
                  {ALL_STATUSES.map(s => {
                    const meta   = SUGGESTION_STATUS_META[s];
                    const active = newStatus === s;
                    return (
                      <button
                        key={s}
                        onClick={() => setNewStatus(s)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-[12px] font-600 cursor-pointer transition-colors ${
                          active
                            ? `${statusDark[s]} border-current`
                            : 'border-white/10 text-white/50 hover:bg-white/5'
                        }`}
                      >
                        <i className={`${meta?.icon ?? 'ri-circle-line'} text-[12px] flex-shrink-0`} />
                        {meta?.label ?? s}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Moderation notes */}
              <div>
                <p className="text-[12px] text-white/40 font-600 uppercase tracking-wider mb-2">Observação (visível ao autor)</p>
                <textarea
                  value={modNotes}
                  onChange={e => setModNotes(e.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder="Explique por que foi recusada, ou descreva os próximos passos..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20 resize-none"
                />
              </div>
            </div>
            <div className="px-5 pb-5 flex gap-3">
              <button
                onClick={() => setSelected(null)}
                className="flex-1 py-2.5 border border-white/10 text-white/60 font-600 rounded-xl text-[13px] cursor-pointer hover:bg-white/5 whitespace-nowrap transition-colors"
              >
                Fechar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white font-600 rounded-xl text-[13px] cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 transition-colors"
              >
                {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirmation modal ──────────────────────────────────────── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
            {deleteStep === 1 ? (
              <>
                <div className="p-6 border-b border-white/10 flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-red-500/15 rounded-xl flex-shrink-0">
                    <i className="ri-delete-bin-line text-red-400 text-[20px]" />
                  </div>
                  <div>
                    <h3 className="text-[16px] font-700 text-white mb-1">Excluir sugestão</h3>
                    <p className="text-[13px] text-white/50 leading-relaxed">
                      Tem certeza que deseja excluir a sugestão
                      {' '}<span className="text-white font-600">&quot;{deleteTarget.title}&quot;</span>?
                      Esta ação é <span className="text-red-400 font-600">irreversível</span> e não poderá ser desfeita.
                    </p>
                  </div>
                </div>
                <div className="p-5 bg-white/[0.02] rounded-b-xl space-y-1.5">
                  <div className="flex items-center gap-2 text-[12px] text-white/40">
                    <i className="ri-thumb-up-line" />
                    <span>{deleteTarget.votes_count} voto{deleteTarget.votes_count !== 1 ? 's' : ''} serão removidos</span>
                  </div>
                  <div className="flex items-center gap-2 text-[12px] text-white/40">
                    <i className="ri-information-line" />
                    <span>Status atual: {SUGGESTION_STATUS_META[deleteTarget.status].label}</span>
                  </div>
                </div>
                <div className="px-5 pb-5 pt-4 flex gap-3">
                  <button
                    onClick={() => setDeleteTarget(null)}
                    className="flex-1 py-2.5 border border-white/10 text-white/60 font-600 rounded-xl text-[13px] cursor-pointer hover:bg-white/5 whitespace-nowrap transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleDeleteConfirmStep1}
                    className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white font-600 rounded-xl text-[13px] cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 transition-colors"
                  >
                    <i className="ri-delete-bin-line" />
                    {requiresExtraConfirm(deleteTarget) ? 'Continuar' : 'Sim, excluir'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="p-6 border-b border-white/10 flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-red-500/20 rounded-xl flex-shrink-0">
                    <i className="ri-alert-line text-red-400 text-[22px]" />
                  </div>
                  <div>
                    <h3 className="text-[16px] font-700 text-red-400 mb-1">Atenção — confirmação extra</h3>
                    <p className="text-[13px] text-white/60 leading-relaxed">
                      Esta sugestão
                      {PUBLISHED_STATUSES.includes(deleteTarget.status) && (
                        <> está <span className="text-emerald-400 font-600">publicada</span> e visível para os usuários</>
                      )}
                      {PUBLISHED_STATUSES.includes(deleteTarget.status) && deleteTarget.votes_count > 0 && ' e'}
                      {deleteTarget.votes_count > 0 && (
                        <> tem <span className="text-white font-600">{deleteTarget.votes_count} voto{deleteTarget.votes_count !== 1 ? 's' : ''}</span> registrados</>
                      )}.
                      {' '}Ao excluir, todos os dados serão perdidos permanentemente.
                    </p>
                  </div>
                </div>
                <div className="px-5 py-4 bg-red-500/5 border-b border-red-500/10">
                  <p className="text-[12px] text-red-300 font-600">Você tem certeza absoluta que deseja excluir?</p>
                  <p className="text-[11px] text-red-400/60 mt-1">Esta ação não pode ser revertida de nenhuma forma.</p>
                </div>
                <div className="px-5 pb-5 pt-4 flex gap-3">
                  <button
                    onClick={() => { setDeleteTarget(null); }}
                    className="flex-1 py-2.5 border border-white/10 text-white/60 font-600 rounded-xl text-[13px] cursor-pointer hover:bg-white/5 whitespace-nowrap transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleDeleteExecute}
                    disabled={deleting}
                    className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-700 rounded-xl text-[13px] cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 transition-colors"
                  >
                    {deleting ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-delete-bin-2-line" />}
                    {deleting ? 'Excluindo...' : 'Excluir definitivamente'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
